import { createContext, type ReactNode, useCallback, useContext, useMemo } from 'react'
import { toast } from 'sonner'

import { useRequestRequoteReview } from '@/models/requote'

interface RequoteOnSave {
  /** Replaces the editor's own save label. */
  label: string
  /** Replaces the editor's own in-progress label. */
  pendingLabel: string
  /** Tells the broker and confirms it to the customer. Call once the save has landed. */
  run: () => Promise<void>
}

const RequoteOnSaveContext = createContext<RequoteOnSave | null>(null)

/**
 * On the quotes page the carriers have already priced the application, so changing an
 * answer is how the customer asks their broker to look again: the editors' save CTAs say
 * so, and the broker decides whether the change is worth re-quoting or belongs on an
 * endorsement. Absent in the chat, where the same editors run before any price exists.
 */
export function RequoteOnSaveProvider({
  applicationId,
  reviewing = false,
  children,
}: {
  applicationId: string
  /** The customer is checking what their broker filled in and hasn't seen a price: edits just save. */
  reviewing?: boolean
  children: ReactNode
}) {
  const { mutateAsync } = useRequestRequoteReview(applicationId)

  const run = useCallback(async () => {
    try {
      const application = await mutateAsync()
      if (reviewing) {
        toast.success('Saved. Your broker will see the change.')
        return
      }
      // Ignored when nothing changed since the carriers priced — don't promise a call back.
      toast.success(
        application.dateRequoteRequested
          ? 'Sent to your broker — they’ll review what changed and be in touch.'
          : 'Saved. Your broker already has everything that’s changed.'
      )
    } catch {
      toast.error(
        reviewing
          ? 'Saved, but we couldn’t let your broker know. Please try again.'
          : 'We couldn’t send your request. Please try again.'
      )
    }
  }, [mutateAsync, reviewing])

  const value = useMemo(
    () =>
      reviewing
        ? { label: 'Save', pendingLabel: 'Saving…', run }
        : { label: 'Request re-quote', pendingLabel: 'Sending…', run },
    [reviewing, run]
  )

  return <RequoteOnSaveContext.Provider value={value}>{children}</RequoteOnSaveContext.Provider>
}

/** Null in the chat, where the caller keeps its own label and its own confirmation. */
export function useRequoteOnSave(): RequoteOnSave | null {
  return useContext(RequoteOnSaveContext)
}
